import {
  EAssetTypeVariant,
  type IconType,
  type TAssetTypeVariant,
} from '@kognitos/lattice';
import type { TFunction } from 'i18next';
import {
  type Commonv1Value,
  type V1Asset,
  type V1Collection,
  V1ComparisonOperator,
  type V1CollectionField,
  type V1CollectionRow,
  V1CollectionStage,
  type V1Folder,
  type V1FolderTreeNode,
  V1LogicalOperator,
  V1SortDirection,
  type V1SortOrder,
  V1ValueScalarType,
} from '@/generated/hooks/bishop/models';
import { V1CollectionUsageType } from '@/generated/hooks/grimoire/models';
import {
  formatBytes,
  parseFileResourceName,
  parseResourceName,
} from '@/shared/utils';
import { getApiErrorInfo } from '@/shared/utils/error-handler';
import {
  decodeNumber,
  formatNumber,
  getResolvedFileDisplayName,
} from '@/modules/outputs/utils';
import type { TResolvedFile } from '@/modules/outputs/types';
import { encodeNumber } from '@/modules/automation-inputs/utils/numberConversion';
import {
  DATE_FORMAT_TOKENS,
  formatDate,
  formatDateDisplay,
  formatDateISO,
  formatDateTimeDisplay,
  formatDateTimeISO,
  formatJsDate,
  isValidDateStrict,
  parseDate,
  parseDateString,
  parseDateTime,
  splitDateRangeISO,
} from '@/shared/utils/datetime-utils';
import { parseTabularFile } from '@/shared/import/parse-tabular-file';
import {
  ERecordFilterOperator,
  type AssetSortDirection,
  type AssetSortField,
  type AssetTypeFilter,
  type CollectionSortDirection,
  type CollectionSortField,
  type IAssetDetailsPanelData,
  type IAssetView,
  type IFolderView,
  type ILibraryItem,
  type IFolderCrumb,
  type ICollectionTemplate,
  type IRecordFilterCondition,
  type IRecordFilterDraft,
  type ISchemaFieldChanges,
  type TAssetScope,
  type TCollectionStageParam,
  type TRecordFilterRowState,
  type TRecordFilterValueShape,
  type TRowFilterOperator,
  type V1RowFilter,
} from './types';

// Maps the UI sort field to the bishop ListAssets/ListFolders `order_by` field.
const SORT_FIELD_TO_ORDER_BY: Record<AssetSortField, string> = {
  name: 'display_name',
  modified: 'update_time',
  created: 'create_time',
};

export function getAssetSortOrderBy(
  field: AssetSortField,
  direction: AssetSortDirection,
): string {
  return `${SORT_FIELD_TO_ORDER_BY[field]} ${direction}`;
}

const COLLECTION_SORT_FIELD_TO_ORDER_BY: Record<CollectionSortField, string> = {
  name: 'display_name',
  modified: 'update_time',
  created: 'create_time',
};

export function getCollectionSortOrderBy(
  field: CollectionSortField,
  direction: CollectionSortDirection,
): string {
  return `${COLLECTION_SORT_FIELD_TO_ORDER_BY[field]} ${direction}`;
}

// Walks the GetFolderTree response and returns the ancestor chain from root down
// to `folderId` (inclusive). Empty when the folder isn't found or at workspace root.
export function getFolderPath(
  roots: readonly V1FolderTreeNode[] | undefined,
  folderId: string | null,
): IFolderCrumb[] {
  if (!folderId || !roots) return [];

  const walk = (
    nodes: readonly V1FolderTreeNode[],
    trail: IFolderCrumb[],
  ): IFolderCrumb[] | null => {
    for (const node of nodes) {
      if (!node.folder) continue;
      const id = parseResourceName(node.folder.name).folder;
      if (!id) continue;
      const crumb: IFolderCrumb = {
        id,
        displayName: node.folder.display_name,
      };
      const nextTrail = [...trail, crumb];
      if (id === folderId) return nextTrail;
      if (node.children?.length) {
        const found = walk(node.children, nextTrail);
        if (found) return found;
      }
    }
    return null;
  };

  return walk(roots, []) ?? [];
}

// Substring passed to bishop's `filter` param (display_name OR mime_type substring
// match). `folders` is the special "show folders only" state — no ListAssets call.
// Documents/spreadsheet substrings only catch modern Office formats today.
export const ASSET_TYPE_FILTERS: ReadonlyArray<{
  value: AssetTypeFilter;
  labelKey: string;
  mimeSubstring: string | null;
}> = [
  {
    value: 'folders',
    labelKey: 'library.assets.filters.typeFolders',
    mimeSubstring: null,
  },
  {
    value: 'pdf',
    labelKey: 'library.assets.filters.typePdf',
    mimeSubstring: 'application/pdf',
  },
  {
    value: 'document',
    labelKey: 'library.assets.filters.typeDocument',
    mimeSubstring: 'wordprocessingml',
  },
  {
    value: 'spreadsheet',
    labelKey: 'library.assets.filters.typeSpreadsheet',
    mimeSubstring: 'sheet',
  },
  {
    value: 'image',
    labelKey: 'library.assets.filters.typeImage',
    mimeSubstring: 'image/',
  },
  {
    value: 'json',
    labelKey: 'library.assets.filters.typeJson',
    mimeSubstring: 'application/json',
  },
];

// Narrow an arbitrary URL query value to a known AssetTypeFilter. Guards the
// `?type=` param so a hand-edited or stale URL can't inject an unsupported type.
export function isAssetTypeFilter(
  value: string | null | undefined,
): value is AssetTypeFilter {
  return value != null && ASSET_TYPE_FILTERS.some(t => t.value === value);
}

export function getAssetTypeMimeSubstring(
  value: AssetTypeFilter | null,
): string | null {
  if (!value) return null;
  return ASSET_TYPE_FILTERS.find(t => t.value === value)?.mimeSubstring ?? null;
}

/**
 * Escapes a user-supplied value for use inside a double-quoted AIP-160 filter
 * clause (e.g. `display_name : "<value>"`). Backslashes are escaped first so
 * the `\` we add in front of each `"` isn't itself double-escaped.
 */
export function escapeFilterValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * File extensions accepted by the asset library upload flow.
 */
export const ALLOWED_ASSET_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  '.txt',
  '.png',
  '.jpg',
  '.jpeg',
  '.md',
  '.json',
] as const;

/**
 * Comma-separated extensions string for the `accept` attribute on file inputs.
 */
export const ACCEPTED_ASSET_FILE_TYPES = ALLOWED_ASSET_EXTENSIONS.join(',');

/**
 * Whether a File's name ends with one of ALLOWED_ASSET_EXTENSIONS.
 * Used to enforce the type allow-list on drag-and-drop, where the
 * `accept` attribute does not filter for us.
 */
export function isAllowedAssetFile(file: File): boolean {
  const lower = file.name.toLowerCase();
  return ALLOWED_ASSET_EXTENSIONS.some(ext => lower.endsWith(ext));
}

/**
 * Tags a V1Folder so the heterogeneous list can be narrowed on `kind`.
 */
export const toFolderView = (folder: V1Folder): IFolderView => ({
  ...folder,
  kind: 'folder',
});

/**
 * Tags a V1Asset so the heterogeneous list can be narrowed on `kind`.
 */
export const toAssetView = (asset: V1Asset): IAssetView => ({
  ...asset,
  kind: 'asset',
});

/**
 * Extracts the bare Bishop file id an asset points at.
 *
 * @returns The file id, or undefined when no usable URI is present.
 */
export function getAssetFileId(
  asset: Pick<V1Asset, 'reference_uri'>,
): string | undefined {
  const uri = asset.reference_uri;
  if (!uri) return undefined;
  if (!uri.startsWith('bishop://')) return undefined;
  const fileId = uri.slice('bishop://'.length);
  if (!fileId || fileId.includes('/')) return undefined;
  return fileId;
}

/**
 * Derives the downloadable file resource name for an asset.
 *
 * @returns The resource name, or undefined when no usable URI is present.
 */
export function getAssetFileResourceName(
  asset: Pick<V1Asset, 'reference_uri'>,
  scope: TAssetScope,
): string | undefined {
  const fileId = getAssetFileId(asset);
  if (!fileId) return undefined;
  return `organizations/${scope.organization}/workspaces/${scope.workspace}/files/${fileId}`;
}

/**
 * Builds a collection's AIP resource name. Needed for the gRPC-shaped RPCs
 * that take a `parent` in the request body rather than path params.
 */
export function getCollectionResourceName(
  organization: string,
  workspace: string,
  collectionId: string,
): string {
  return `organizations/${organization}/workspaces/${workspace}/collections/${collectionId}`;
}

/**
 * Formats a byte count into a compact display label.
 *
 * @param {string | undefined} sizeBytes - Size in bytes as returned by the API
 * @returns {string | undefined} Human-readable size label
 */
export function formatAssetSize(sizeBytes?: string): string | undefined {
  return formatBytes(sizeBytes) ?? undefined;
}

/**
 * Formats an ISO timestamp for asset details.
 *
 * @param {string | undefined} timestamp - ISO timestamp
 * @returns {string | undefined} Display timestamp
 */
export function formatAssetTimestamp(timestamp?: string): string | undefined {
  if (!timestamp) return undefined;
  return formatDate(
    parseDateString(timestamp),
    DATE_FORMAT_TOKENS.DATE_AT_TIME,
  );
}

/**
 * Creates a type label from a library item.
 */
export function getAssetTypeLabel(
  item: ILibraryItem,
  folderLabel: string,
): string | undefined {
  if (item.kind === 'folder') return folderLabel;
  if (!item.mime_type) return undefined;

  const subtype = item.mime_type.split('/').at(1);
  if (!subtype) return item.mime_type;

  if (subtype.includes('pdf')) return 'PDF';
  if (subtype.includes('png')) return 'PNG';
  if (subtype.includes('jpeg') || subtype.includes('jpg')) return 'JPEG';
  if (subtype.includes('csv')) return 'CSV';
  if (subtype.includes('sheet')) return 'XLSX';
  if (subtype.includes('word')) return 'DOCX';
  if (subtype.includes('json')) return 'JSON';

  return subtype.toUpperCase();
}

/**
 * Maps a library item to the details panel data shape.
 */
export function libraryItemToDetailsPanelData(
  item: ILibraryItem,
  labels: { folderType: string; defaultLocation: string },
): IAssetDetailsPanelData {
  const isAsset = item.kind === 'asset';
  return {
    name: item.display_name,
    description: isAsset ? item.description : undefined,
    tags: isAsset ? item.tags : undefined,
    type: getAssetTypeLabel(item, labels.folderType),
    size: isAsset ? formatAssetSize(item.size_bytes) : undefined,
    location: labels.defaultLocation,
    createdBy: item.created_by,
    createdAt: formatAssetTimestamp(item.create_time),
    modified: formatAssetTimestamp(item.update_time),
    reference: isAsset ? item.reference_uri : undefined,
    previewUrl: isAsset ? item.preview_url : undefined,
  };
}

// TODO: Placeholder templates until real ones are defined.
export const COLLECTION_TEMPLATES: ReadonlyArray<ICollectionTemplate> = [
  {
    id: 'customer-lookup',
    nameKey: 'library.collections.templates.customerLookup.name',
    descriptionKey: 'library.collections.templates.customerLookup.description',
    icon: 'UserRound',
  },
  {
    id: 'vendor-directory',
    nameKey: 'library.collections.templates.vendorDirectory.name',
    descriptionKey: 'library.collections.templates.vendorDirectory.description',
    icon: 'Building',
  },
  {
    id: 'status-mapping',
    nameKey: 'library.collections.templates.statusMapping.name',
    descriptionKey: 'library.collections.templates.statusMapping.description',
    icon: 'Workflow',
  },
];

/**
 * @param mime - The MIME type to get the asset type from
 * @returns The asset type variant
 */
export function getAssetTypeFromMimeType(mime?: string): TAssetTypeVariant {
  if (!mime) return EAssetTypeVariant.Unknown;
  if (mime.startsWith('image/')) return EAssetTypeVariant.Image;
  if (mime === 'application/pdf') return EAssetTypeVariant.PDF;
  if (mime === 'application/json') return EAssetTypeVariant.JSON;
  if (
    mime === 'text/csv' ||
    mime === 'application/vnd.ms-excel' ||
    mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return EAssetTypeVariant.Spreadsheet;
  }
  if (
    mime === 'application/msword' ||
    mime ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mime === 'text/plain'
  ) {
    return EAssetTypeVariant.Document;
  }
  return EAssetTypeVariant.Unknown;
}

/**
 * Icon standing in for each collection field type, shared by the schema table
 * and the field editor's Type select so a type reads the same in both.
 */
// TODO: add a JSON icon entry once bishop adds VALUE_SCALAR_TYPE_JSON.
export const FIELD_TYPE_ICON: Partial<Record<V1ValueScalarType, IconType>> = {
  [V1ValueScalarType.VALUE_SCALAR_TYPE_TEXT]: 'Type',
  [V1ValueScalarType.VALUE_SCALAR_TYPE_NUMBER]: 'Hash',
  [V1ValueScalarType.VALUE_SCALAR_TYPE_BOOLEAN]: 'ToggleRight',
  [V1ValueScalarType.VALUE_SCALAR_TYPE_DATE]: 'Calendar',
  [V1ValueScalarType.VALUE_SCALAR_TYPE_DATETIME]: 'Clock',
  [V1ValueScalarType.VALUE_SCALAR_TYPE_FILE]: 'FileText',
};

/**
 * Translation key naming how an automation accesses a collection. UNSPECIFIED
 * means grimoire couldn't determine the access from the code manifest, so it
 * has no label and reads as empty.
 */
export const COLLECTION_USAGE_LABEL_KEY: Partial<
  Record<V1CollectionUsageType, string>
> = {
  [V1CollectionUsageType.COLLECTION_USAGE_TYPE_READ]:
    'library.collection.usage.access.read',
  [V1CollectionUsageType.COLLECTION_USAGE_TYPE_WRITE]:
    'library.collection.usage.access.write',
};

/**
 * Translation key naming each collection field type, alongside
 * {@link FIELD_TYPE_ICON} so a type reads the same wherever it is shown — the
 * schema table, the field editor's Type select, and quill2's approval card.
 */
export const FIELD_TYPE_LABEL_KEY: Record<V1ValueScalarType, string> = {
  [V1ValueScalarType.VALUE_SCALAR_TYPE_UNSPECIFIED]:
    'library.collection.field.type.text',
  [V1ValueScalarType.VALUE_SCALAR_TYPE_TEXT]:
    'library.collection.field.type.text',
  [V1ValueScalarType.VALUE_SCALAR_TYPE_NUMBER]:
    'library.collection.field.type.number',
  [V1ValueScalarType.VALUE_SCALAR_TYPE_BOOLEAN]:
    'library.collection.field.type.boolean',
  [V1ValueScalarType.VALUE_SCALAR_TYPE_DATE]:
    'library.collection.field.type.date',
  [V1ValueScalarType.VALUE_SCALAR_TYPE_DATETIME]:
    'library.collection.field.type.datetime',
  [V1ValueScalarType.VALUE_SCALAR_TYPE_TIME]:
    'library.collection.field.type.time',
  [V1ValueScalarType.VALUE_SCALAR_TYPE_FILE]:
    'library.collection.field.type.file',
  [V1ValueScalarType.VALUE_SCALAR_TYPE_TIMEDELTA]:
    'library.collection.field.type.timedelta',
};

/**
 * Field types a user can pick for a collection field, in display order. Narrower
 * than {@link V1ValueScalarType} — the remaining types are readable but not
 * offered when creating or importing a schema.
 */
// TODO: add a JSON entry once bishop adds V1ValueScalarType.VALUE_SCALAR_TYPE_JSON.
export const SELECTABLE_FIELD_TYPES = [
  V1ValueScalarType.VALUE_SCALAR_TYPE_TEXT,
  V1ValueScalarType.VALUE_SCALAR_TYPE_NUMBER,
  V1ValueScalarType.VALUE_SCALAR_TYPE_BOOLEAN,
  V1ValueScalarType.VALUE_SCALAR_TYPE_DATE,
  V1ValueScalarType.VALUE_SCALAR_TYPE_DATETIME,
  V1ValueScalarType.VALUE_SCALAR_TYPE_FILE,
];

/**
 * Renders a `Commonv1Value` as a display string for a collection schema's
 * default-value cell. Probes oneof fields in declaration order so it works
 * without a known scalar type. Returns `undefined` for unset/null/empty so
 * callers can substitute their own placeholder (typically the translated
 * `library.details.placeholder.empty`).
 */
export function formatCollectionDefaultValue(
  value: Commonv1Value | undefined,
): string | undefined {
  if (!value || value.null_value !== undefined) return undefined;
  if (value.text !== undefined) return value.text || undefined;
  if (value.bool_value !== undefined)
    return value.bool_value ? 'true' : 'false';
  if (value.number) return formatNumber(value.number);
  if (value.date) return formatDateDisplay(value.date);
  if (value.datetime) return formatDateTimeDisplay(value.datetime);
  const file = toResolvedFileValue(value);
  if (file) return getResolvedFileDisplayName(file);
  return undefined;
}

/**
 * Reshapes a file-typed collection cell into the `TResolvedFile` the outputs
 * module's `ValueChip` renders, so a record's file reads and previews exactly
 * as a run's file input does.
 *
 * @param value - Cell value from a `V1CollectionRow`
 * @returns The resolved file, or undefined when the cell holds no file
 */
export function toResolvedFileValue(
  value: Commonv1Value | undefined,
): TResolvedFile | undefined {
  if (value?.file?.remote) {
    return { type: 'file-remote', fileUrl: value.file.remote };
  }
  const inline = value?.file?.inline;
  if (inline?.name) {
    return {
      type: 'file-inline',
      name: inline.name,
      content: inline.content ?? '',
    };
  }
  return undefined;
}

/**
 * Reads a CSV/XLSX/JSON file and returns the column header names, trimmed and
 * stripped of empty entries. Used by the schema-import flow to turn a source
 * file into one collection field per column. Repeated names keep only their
 * first occurrence, since a collection can't hold two fields with one name.
 */
export async function parseSchemaHeaders(file: File): Promise<string[]> {
  const seen = new Set<string>();
  for (const header of (await parseTabularFile(file)).headers) {
    const name = header.trim();
    if (name.length > 0) seen.add(name);
  }
  return [...seen];
}

/**
 * Encodes a form-state default-value string into the `Commonv1Value` shape
 * bishop expects on the matching `V1CollectionField.default_value`. Returns
 * `undefined` when the input is empty or can't be safely represented — the
 * caller drops the property so bishop keeps the existing default.
 */
export function encodeFieldDefaultValue(
  type: V1ValueScalarType,
  defaultValue: string,
): Commonv1Value | undefined {
  const trimmed = defaultValue.trim();
  if (trimmed.length === 0) return undefined;
  try {
    switch (type) {
      case V1ValueScalarType.VALUE_SCALAR_TYPE_TEXT:
        return { text: trimmed };
      case V1ValueScalarType.VALUE_SCALAR_TYPE_NUMBER: {
        if (!Number.isFinite(Number(trimmed))) return undefined;
        return { number: encodeNumber(trimmed) };
      }
      case V1ValueScalarType.VALUE_SCALAR_TYPE_BOOLEAN: {
        if (trimmed === 'true') return { bool_value: true };
        if (trimmed === 'false') return { bool_value: false };
        return undefined;
      }
      case V1ValueScalarType.VALUE_SCALAR_TYPE_DATE:
        return { date: parseDate(trimmed) };
      case V1ValueScalarType.VALUE_SCALAR_TYPE_DATETIME: {
        // The form may carry only a date ('YYYY-MM-DD') or a full
        // 'YYYY-MM-DDTHH:mm'. Pad with midnight when time is missing so the
        // parser doesn't reject it.
        const padded = /T\d{2}:\d{2}$/.test(trimmed)
          ? trimmed
          : `${trimmed}T00:00`;
        return { datetime: parseDateTime(padded) };
      }
      // The `file` detail item uploads on pick, so the form normally holds a
      // bishop file resource name by now. Anything else — notably the bare
      // filename an inline cell decodes to — is not a remote path and must not
      // be written as one; callers drop the key and keep the existing cell.
      case V1ValueScalarType.VALUE_SCALAR_TYPE_FILE:
        return parseFileResourceName(trimmed)
          ? { file: { remote: trimmed } }
          : undefined;
      default:
        return undefined;
    }
  } catch {
    return undefined;
  }
}

/**
 * Inverse of `encodeFieldDefaultValue`: takes a schema field and produces a
 * machine-readable form string (ISO date / plain numeric / 'true'/'false' /
 * text) that a subsequent re-encode will round-trip cleanly.
 */
export function decodeCellValueToForm(
  v: Commonv1Value | undefined,
  scalarType: V1ValueScalarType | undefined,
): string {
  if (!v || v.null_value !== undefined) return '';
  switch (scalarType) {
    case V1ValueScalarType.VALUE_SCALAR_TYPE_TEXT:
      return v.text ?? '';
    case V1ValueScalarType.VALUE_SCALAR_TYPE_NUMBER:
      return v.number ? String(decodeNumber(v.number)) : '';
    case V1ValueScalarType.VALUE_SCALAR_TYPE_BOOLEAN:
      if (v.bool_value === undefined) return '';
      return v.bool_value ? 'true' : 'false';
    case V1ValueScalarType.VALUE_SCALAR_TYPE_DATE:
      return v.date ? formatDateISO(v.date) : '';
    case V1ValueScalarType.VALUE_SCALAR_TYPE_DATETIME:
      return v.datetime ? formatDateTimeISO(v.datetime) : '';
    case V1ValueScalarType.VALUE_SCALAR_TYPE_FILE:
      return v.file?.remote ?? v.file?.inline?.name ?? '';
    default:
      return '';
  }
}

export function decodeFieldDefaultToForm(field: V1CollectionField): string {
  return decodeCellValueToForm(field.default_value, field.type?.scalar_type);
}

/**
 * Pulls a collection row's `__row_id__` cell out as a string. Bishop returns
 * the id as a `Commonv1Value` whose actual scalar (text vs number) depends
 * on the collection's storage backend, so callers can't assume one shape.
 *
 * Returns `undefined` when no row id is present. Callers that need a
 * non-nullable string (e.g. AG-Grid's `getRowId`) can `?? ''`.
 */
export function getCollectionRowId(row: V1CollectionRow): string | undefined {
  const rid = row.fields?.__row_id__;
  if (!rid) return undefined;
  if (rid.text !== undefined) return rid.text;
  if (rid.number) return String(decodeNumber(rid.number));
  return undefined;
}

// Fixed key order and defaults so omitted-vs-false and serializer key order
// don't read as edits.
const fieldSignature = (field: V1CollectionField) =>
  JSON.stringify([
    field.type?.scalar_type ?? null,
    field.description ?? '',
    !!field.required,
    !!field.unique,
    field.default_value ?? null,
  ]);

/**
 * Diffs a collection's draft schema fields against its published ones, by field
 * name. Fields only in the draft are added; fields in both with differing type,
 * description, required, unique, or default are edited.
 */
export function getSchemaFieldChanges(
  draftFields: readonly V1CollectionField[] = [],
  publishedFields: readonly V1CollectionField[] = [],
): ISchemaFieldChanges {
  const publishedByName = new Map(publishedFields.map(f => [f.name, f]));
  const added: V1CollectionField[] = [];
  const edited: V1CollectionField[] = [];
  draftFields.forEach(field => {
    const publishedField = publishedByName.get(field.name);
    if (!publishedField) added.push(field);
    else if (fieldSignature(publishedField) !== fieldSignature(field))
      edited.push(field);
  });
  // No removed bucket: published fields can't be deleted, for backward compatibility.
  return { added, edited };
}

/**
 * Whether a collection's draft schema is ahead of its published schema, i.e.
 * there are schema edits left to publish.
 */
export function hasSchemaChanges(
  draft: V1Collection | undefined,
  published: V1Collection | undefined,
): boolean {
  return (draft?.schema_version ?? 0) > (published?.schema_version ?? 0);
}

/** Maps a collection's stage to the collection page's `?stage=` URL param. */
export function toCollectionStageParam(
  stage: V1CollectionStage | undefined,
): TCollectionStageParam {
  return stage === V1CollectionStage.COLLECTION_STAGE_PUBLISHED
    ? 'published'
    : 'draft';
}

/** Fallback page size when the viewport isn't measurable yet. */
export const RECORDS_PAGE_SIZE = 10;

/** By default, list newest records first. `__row_id__` is bishop's insertion-ordered row id. */
export const DEFAULT_RECORDS_SORT: V1SortOrder = {
  field: '__row_id__',
  direction: V1SortDirection.SORT_DIRECTION_DESCENDING,
};
/** Matches `RecordsTable` AG-Grid `rowHeight`. */
export const RECORDS_TABLE_ROW_HEIGHT = 52;

/**
 * Vertical chrome above/below the record rows (content header, tabs, filter
 * bar, column header, pagination) reserved out of the viewport so page size
 * only fills the remaining height — same idea as the audit log table.
 */
export const RECORDS_TABLE_CHROME_HEIGHT = 360;

export const RECORDS_PAGE_SIZE_MIN = 5;
export const RECORDS_PAGE_SIZE_MAX = 50;

/**
 * How many record rows fit in the current viewport. Clamped so tiny windows
 * still show a usable page and huge monitors don't request an unbounded page.
 */
export function computeRecordsPageSize(viewportHeight: number): number {
  if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) {
    return RECORDS_PAGE_SIZE;
  }
  const fitted = Math.floor(
    (viewportHeight - RECORDS_TABLE_CHROME_HEIGHT) / RECORDS_TABLE_ROW_HEIGHT,
  );
  return Math.min(
    RECORDS_PAGE_SIZE_MAX,
    Math.max(RECORDS_PAGE_SIZE_MIN, fitted),
  );
}

/**
 * Derives the page total from a record count: collection metadata when
 * unfiltered (may lag writes), ListCollectionRows `total_size` when filtered.
 * @param options - Record count and actual query page size.
 * @returns Page total, or undefined when no usable count exists.
 */
export function resolveCollectionRecordsPageTotal({
  recordCount,
  pageSize,
}: {
  recordCount: number | string | undefined;
  pageSize: number;
}): number | undefined {
  if (pageSize <= 0) return undefined;
  if (recordCount === undefined || recordCount === '') return undefined;
  const count =
    typeof recordCount === 'number' ? recordCount : Number(recordCount);
  if (!Number.isFinite(count) || count < 0) return undefined;
  return Math.max(1, Math.ceil(count / pageSize));
}

/**
 * The records table's sort as AG-Grid's per-column `sort`, `null` for every
 * column but the sorted one. Bishop treats `SORT_DIRECTION_UNSPECIFIED` as
 * ascending, so only DESCENDING reads as 'desc'.
 */
export function getRecordsSortDirection(
  colKey: string,
  sort: V1SortOrder | undefined,
): 'asc' | 'desc' | null {
  if (!sort || sort.field !== colKey) return null;
  return sort.direction === V1SortDirection.SORT_DIRECTION_DESCENDING
    ? 'desc'
    : 'asc';
}

/** A sorted AG-Grid column as bishop's `structured_order_by` entry. */
export function toRecordsSortOrder(
  colId: string,
  direction: 'asc' | 'desc',
): V1SortOrder {
  return {
    field: colId,
    direction:
      direction === 'desc'
        ? V1SortDirection.SORT_DIRECTION_DESCENDING
        : V1SortDirection.SORT_DIRECTION_ASCENDING,
  };
}

/**
 * Sort arrows for the records grid's `icons`. Lattice's grid theme ships
 * AG-Grid's *bold* Quartz icon set, which strokes them at 2px; these are the
 * same shapes a pixel thinner. AG-Grid builds these outside React, hence markup
 * strings rather than a lattice `Icon`.
 */
const thinSortIcon = (...paths: string[]) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">${paths
    .map(d => `<path d="${d}" vector-effect="non-scaling-stroke"/>`)
    .join('')}</svg>`;

export const RECORDS_SORT_ICONS = {
  sortAscending: thinSortIcon('m5 12 7-7 7 7', 'M12 19V5'),
  sortDescending: thinSortIcon('M12 5v14', 'm19 12-7 7-7-7'),
  sortUnSort: thinSortIcon('m7 15 5 5 5-5', 'm7 9 5-5 5 5'),
};

const FILLER_ROW_ID_PREFIX = '__filler__';

/**
 * Blank rows padding a short page out to `RECORDS_PAGE_SIZE`, so the table is
 * the same height on every page. They carry only a row id, which keeps them
 * distinct for AG-Grid's `getRowId` and leaves every cell empty.
 */
export function buildFillerRows(count: number): V1CollectionRow[] {
  return Array.from({ length: Math.max(0, count) }, (_, index) => ({
    fields: { __row_id__: { text: `${FILLER_ROW_ID_PREFIX}${index}` } },
  }));
}

/** A padded blank row has no record behind it, so it can't be edited or acted on. */
export function isFillerRow(row: V1CollectionRow | undefined): boolean {
  if (!row) return false;
  return !!getCollectionRowId(row)?.startsWith(FILLER_ROW_ID_PREFIX);
}

/**
 * Returns the contiguous slice of `orderedIds` between `anchorId` and `targetId`
 * (inclusive, order-agnostic), used for shift-click range selection. Empty when
 * either id is missing from the list.
 */
export function getSelectionRange(
  orderedIds: string[],
  anchorId: string,
  targetId: string,
): string[] {
  const start = orderedIds.indexOf(anchorId);
  const end = orderedIds.indexOf(targetId);
  if (start === -1 || end === -1) return [];
  const [from, to] = start < end ? [start, end] : [end, start];
  return orderedIds.slice(from, to + 1);
}

/**
 * Bishop's stable ErrorInfo `reason` codes → the i18n key prefix for
 * their toast copy, each with a `.title` and `.description`.
 */
const COLLECTION_ERROR_COPY: Record<string, string> = {
  SCHEMA_BACKWARD_INCOMPATIBLE:
    'library.collections.errors.schemaBackwardIncompatible',
  FIELD_ALREADY_EXISTS: 'library.collections.errors.fieldAlreadyExists',
  SCHEMA_INVALID: 'library.collections.errors.schemaInvalid',
  ROW_SCHEMA_MISMATCH: 'library.collections.errors.rowSchemaMismatch',
  COLLECTION_STAGE_NOT_FOUND: 'library.collections.errors.stageNotPublished',
};

/**
 * Toast copy for a failed collection operation.
 *
 * Looks the ErrorInfo `reason` up in `COLLECTION_ERROR_COPY` — never the status
 * code (reasons share codes) or the message text. Anything unmapped falls back
 * to `<fallbackPrefix>`, so reasons bishop adds later degrade to the generic
 * toast.
 */
export function getCollectionErrorToast(
  error: unknown,
  t: TFunction,
  fallbackPrefix: string,
  fallbackOptions?: Record<string, string>,
): { title: string; description: string } {
  const info = getApiErrorInfo(error);
  const prefix = info?.reason ? COLLECTION_ERROR_COPY[info.reason] : undefined;

  if (prefix) {
    // Typed copy interpolates the ErrorInfo metadata (`field`, `column`, …).
    const values = { replace: info?.metadata };
    const typed = {
      title: t(`${prefix}.title`, values),
      description: t(`${prefix}.description`, values),
    };
    // i18next leaves `{{ field }}` verbatim when the value is missing, so an
    // unresolved placeholder means this reason arrived without the metadata its
    // copy names. Better the generic toast than one quoting template syntax.
    if (!`${typed.title}${typed.description}`.includes('{{')) return typed;
  }

  return {
    title: t(`${fallbackPrefix}.title`, fallbackOptions),
    description: t(`${fallbackPrefix}.description`, fallbackOptions),
  };
}

/* -------------------------------------------------------------------------- */
/*                              Record filters                                */
/* -------------------------------------------------------------------------- */
/**
 * Backs the records filter builder: which operators each column type offers, how
 * to label them, whether a row the user is still typing counts yet, and how the
 * finished rows compile into the `bishop.files.v1.RowFilter` the page sends as
 * `ListCollectionRows.structured_filter`.
 */

/** Operators offered per field type, in picker order. Types absent here aren't filterable. */
export const FILTER_OPERATORS_BY_TYPE: Partial<
  Record<V1ValueScalarType, TRowFilterOperator[]>
> = {
  [V1ValueScalarType.VALUE_SCALAR_TYPE_TEXT]: [
    V1ComparisonOperator.COMPARISON_OPERATOR_HAS,
    ERecordFilterOperator.NotHas,
    V1ComparisonOperator.COMPARISON_OPERATOR_EQUAL,
    V1ComparisonOperator.COMPARISON_OPERATOR_NOT_EQUAL,
    ERecordFilterOperator.FuzzyMatch,
    V1ComparisonOperator.COMPARISON_OPERATOR_IN,
    ERecordFilterOperator.NotIn,
  ],
  [V1ValueScalarType.VALUE_SCALAR_TYPE_NUMBER]: [
    V1ComparisonOperator.COMPARISON_OPERATOR_EQUAL,
    V1ComparisonOperator.COMPARISON_OPERATOR_NOT_EQUAL,
    V1ComparisonOperator.COMPARISON_OPERATOR_LESS_THAN,
    V1ComparisonOperator.COMPARISON_OPERATOR_GREATER_THAN,
    V1ComparisonOperator.COMPARISON_OPERATOR_LESS_THAN_OR_EQUAL,
    V1ComparisonOperator.COMPARISON_OPERATOR_GREATER_THAN_OR_EQUAL,
    V1ComparisonOperator.COMPARISON_OPERATOR_IN,
    ERecordFilterOperator.NotIn,
  ],
  [V1ValueScalarType.VALUE_SCALAR_TYPE_BOOLEAN]: [
    V1ComparisonOperator.COMPARISON_OPERATOR_EQUAL,
  ],
  [V1ValueScalarType.VALUE_SCALAR_TYPE_DATE]: [
    V1ComparisonOperator.COMPARISON_OPERATOR_EQUAL,
    V1ComparisonOperator.COMPARISON_OPERATOR_NOT_EQUAL,
    V1ComparisonOperator.COMPARISON_OPERATOR_LESS_THAN,
    V1ComparisonOperator.COMPARISON_OPERATOR_GREATER_THAN,
    V1ComparisonOperator.COMPARISON_OPERATOR_LESS_THAN_OR_EQUAL,
    V1ComparisonOperator.COMPARISON_OPERATOR_GREATER_THAN_OR_EQUAL,
    ERecordFilterOperator.Between,
  ],
};

/** Operator a row starts with for each field type. */
export const FILTER_DEFAULT_OPERATOR_BY_TYPE: Partial<
  Record<V1ValueScalarType, TRowFilterOperator>
> = {
  [V1ValueScalarType.VALUE_SCALAR_TYPE_TEXT]:
    V1ComparisonOperator.COMPARISON_OPERATOR_HAS,
  [V1ValueScalarType.VALUE_SCALAR_TYPE_NUMBER]:
    V1ComparisonOperator.COMPARISON_OPERATOR_EQUAL,
  [V1ValueScalarType.VALUE_SCALAR_TYPE_BOOLEAN]:
    V1ComparisonOperator.COMPARISON_OPERATOR_EQUAL,
  [V1ValueScalarType.VALUE_SCALAR_TYPE_DATE]:
    V1ComparisonOperator.COMPARISON_OPERATOR_EQUAL,
};

/** Operator label i18n keys, under `library.collection.records.filters.operator`. */
export const FILTER_OPERATOR_LABEL_KEY: Record<TRowFilterOperator, string> = {
  [V1ComparisonOperator.COMPARISON_OPERATOR_EQUAL]: 'equal',
  [V1ComparisonOperator.COMPARISON_OPERATOR_NOT_EQUAL]: 'notEqual',
  [V1ComparisonOperator.COMPARISON_OPERATOR_LESS_THAN]: 'lessThan',
  [V1ComparisonOperator.COMPARISON_OPERATOR_LESS_THAN_OR_EQUAL]:
    'lessThanOrEqual',
  [V1ComparisonOperator.COMPARISON_OPERATOR_GREATER_THAN]: 'greaterThan',
  [V1ComparisonOperator.COMPARISON_OPERATOR_GREATER_THAN_OR_EQUAL]:
    'greaterThanOrEqual',
  [V1ComparisonOperator.COMPARISON_OPERATOR_HAS]: 'has',
  [ERecordFilterOperator.NotHas]: 'notHas',
  [V1ComparisonOperator.COMPARISON_OPERATOR_IN]: 'in',
  [ERecordFilterOperator.NotIn]: 'notIn',
  [ERecordFilterOperator.FuzzyMatch]: 'fuzzyMatch',
  [ERecordFilterOperator.Between]: 'between',
};

/**
 * Label overrides for date fields, where "before"/"after" read better than
 * "less than"/"greater than". Falls back to {@link FILTER_OPERATOR_LABEL_KEY}.
 */
const DATE_FILTER_OPERATOR_LABEL_KEY: Partial<
  Record<TRowFilterOperator, string>
> = {
  [V1ComparisonOperator.COMPARISON_OPERATOR_EQUAL]: 'is',
  [V1ComparisonOperator.COMPARISON_OPERATOR_LESS_THAN]: 'before',
  [V1ComparisonOperator.COMPARISON_OPERATOR_GREATER_THAN]: 'after',
  [V1ComparisonOperator.COMPARISON_OPERATOR_LESS_THAN_OR_EQUAL]: 'onOrBefore',
  [V1ComparisonOperator.COMPARISON_OPERATOR_GREATER_THAN_OR_EQUAL]: 'onOrAfter',
};

/** The operator's label i18n key for this field type, under `library.collection.records.filters.operator`. */
export function getFilterOperatorLabelKey(
  operator: TRowFilterOperator,
  type: V1ValueScalarType | undefined,
): string {
  const dateKey =
    type === V1ValueScalarType.VALUE_SCALAR_TYPE_DATE
      ? DATE_FILTER_OPERATOR_LABEL_KEY[operator]
      : undefined;
  return dateKey ?? FILTER_OPERATOR_LABEL_KEY[operator];
}

/** Operators that pair a field with a comma-separated list of values rather than one. */
export function isListOperator(operator: TRowFilterOperator): boolean {
  return (
    operator === V1ComparisonOperator.COMPARISON_OPERATOR_IN ||
    operator === ERecordFilterOperator.NotIn
  );
}

/**
 * What an operator's value holds: one value, a comma-separated `list`, or a
 * `from/to` `range`. A row's value only carries over an operator change when
 * both operators share a shape.
 */
export function getFilterValueShape(
  operator: TRowFilterOperator,
): TRecordFilterValueShape {
  if (isListOperator(operator)) return 'list';
  if (operator === ERecordFilterOperator.Between) return 'range';
  return 'single';
}

/** The real bishop comparison a synthetic negated operator wraps in a `negation` node. */
const NEGATED_COMPARISON_OPERATOR: Partial<
  Record<TRowFilterOperator, V1ComparisonOperator>
> = {
  [ERecordFilterOperator.NotHas]: V1ComparisonOperator.COMPARISON_OPERATOR_HAS,
  [ERecordFilterOperator.NotIn]: V1ComparisonOperator.COMPARISON_OPERATOR_IN,
};

export function getFilterOperators(
  type: V1ValueScalarType | undefined,
): TRowFilterOperator[] {
  return (type && FILTER_OPERATORS_BY_TYPE[type]) ?? [];
}

/** The operator a row starts with when it's seeded or switches to this field type. */
export function getDefaultFilterOperator(
  type: V1ValueScalarType | undefined,
): TRowFilterOperator {
  return (
    (type && FILTER_DEFAULT_OPERATOR_BY_TYPE[type]) ??
    getFilterOperators(type)[0] ??
    V1ComparisonOperator.COMPARISON_OPERATOR_EQUAL
  );
}

/**
 * Checks if a field is able to be filtered based on its type.
 * Right now, the builder supports Text, Number, Boolean, and Date types.
 */
export function isFilterableFieldType(
  type: V1ValueScalarType | undefined,
): boolean {
  return getFilterOperators(type).length > 0;
}

/**
 * A boolean condition is complete the moment its operator is picked; everything
 * else needs a value typed in.
 */
function isConditionComplete(
  draft: IRecordFilterDraft,
  type: V1ValueScalarType | undefined,
): boolean {
  if (!isFilterableFieldType(type)) return false;
  if (type === V1ValueScalarType.VALUE_SCALAR_TYPE_BOOLEAN) return true;
  return draft.value.trim().length > 0;
}

/**
 * Encodes a row's typed-in value as the `Commonv1Value` a `Comparison` carries,
 * per the field's scalar type. "Is any of" splits on commas into a `list`.
 *
 * @returns The encoded value, or undefined when it can't represent what the user
 * typed — a number field given `abc`, or a list where any single item fails.
 */
function toComparisonValue(
  draft: IRecordFilterDraft,
  type: V1ValueScalarType,
): Commonv1Value | undefined {
  if (!isListOperator(draft.operator)) {
    return encodeFieldDefaultValue(type, draft.value);
  }

  const items: Commonv1Value[] = [];
  for (const part of draft.value.split(',')) {
    if (part.trim().length === 0) continue;
    const item = encodeFieldDefaultValue(type, part);
    if (!item) return undefined;
    items.push(item);
  }
  return items.length > 0 ? { list: { items } } : undefined;
}

/**
 * Compiles a date row. `Between` becomes an AND of `>= from` and `<= to`, so
 * both ends are included.
 */
function toDateFilterNode(draft: IRecordFilterDraft): V1RowFilter | undefined {
  const compare = (
    operator: V1ComparisonOperator,
    date: string,
  ): V1RowFilter => ({
    comparison: {
      field: draft.field,
      operator,
      value: { date: parseDate(date) },
    },
  });

  if (draft.operator === ERecordFilterOperator.Between) {
    const { from, to } = splitDateRangeISO(draft.value.trim());
    if (!isValidDateStrict(from) || !isValidDateStrict(to)) return undefined;
    return {
      logical: {
        operator: V1LogicalOperator.LOGICAL_OPERATOR_AND,
        operands: [
          compare(
            V1ComparisonOperator.COMPARISON_OPERATOR_GREATER_THAN_OR_EQUAL,
            from,
          ),
          compare(
            V1ComparisonOperator.COMPARISON_OPERATOR_LESS_THAN_OR_EQUAL,
            to,
          ),
        ],
      },
    };
  }

  const date = draft.value.trim();
  if (!isValidDateStrict(date)) return undefined;
  return compare(draft.operator as V1ComparisonOperator, date);
}

/** Similarity cutoff for the fuzzy filter, looser than bishop's 0.8 default so near matches show up for review. */
const FUZZY_MATCH_THRESHOLD = 0.6;

/** Compiles one row into its `RowFilter` node, or undefined if it can't be. */
function toFilterNode(
  draft: IRecordFilterDraft,
  type: V1ValueScalarType | undefined,
): V1RowFilter | undefined {
  if (!type || !getFilterOperators(type).includes(draft.operator)) {
    return undefined;
  }
  if (!isConditionComplete(draft, type)) return undefined;

  if (draft.operator === ERecordFilterOperator.FuzzyMatch) {
    return {
      fuzzy_match: {
        field: draft.field,
        query: draft.value.trim(),
        threshold: FUZZY_MATCH_THRESHOLD,
      },
    };
  }
  if (type === V1ValueScalarType.VALUE_SCALAR_TYPE_DATE) {
    return toDateFilterNode(draft);
  }

  const value = toComparisonValue(draft, type);
  if (!value) return undefined;

  // NOT_HAS/NOT_IN aren't real bishop operators — negate the HAS/IN node instead.
  const negatedOperator = NEGATED_COMPARISON_OPERATOR[draft.operator];
  const node: V1RowFilter = {
    comparison: {
      field: draft.field,
      operator: negatedOperator ?? (draft.operator as V1ComparisonOperator),
      value,
    },
  };
  return negatedOperator ? { negation: node } : node;
}

/**
 * Whether a row can be saved — false when it's still half-typed, or when the
 * value can't encode for the field's type ("abc" on a number column). Gates the
 * row's Save button.
 */
export function isFilterConditionValid(
  draft: IRecordFilterDraft,
  type: V1ValueScalarType | undefined,
): boolean {
  return toFilterNode(draft, type) !== undefined;
}

/**
 * Rewinds the builder to what bishop is actually filtering on: rows revert to their
 * applied snapshot and never-saved rows are dropped whenever the popover is closed, then reopened.
 */
export function discardUnsavedFilters(
  conditions: IRecordFilterCondition[],
): IRecordFilterCondition[] {
  return conditions.reduce<IRecordFilterCondition[]>((acc, condition) => {
    if (condition.applied) acc.push({ ...condition, ...condition.applied });
    return acc;
  }, []);
}

/**
 * A filter is pruned when its column was deleted or renamed (no type to look up),
 * retyped to something that can't be filtered, or retyped such that its operator or value no longer fits.
 *
 * Never-saved rows are dropped too; they're transient, same as on popover close.
 */
export function pruneInvalidFilters(
  conditions: IRecordFilterCondition[],
  typeByField: Record<string, V1ValueScalarType | undefined>,
): IRecordFilterCondition[] {
  return conditions.filter(
    ({ applied }) =>
      applied && isFilterConditionValid(applied, typeByField[applied.field]),
  );
}

/**
 * Compares a row against its applied snapshot to decide whether it has anything
 * to save. Drives the row's Save button, and tells `handleOpenChange` whether
 * closing the popover has unsaved work to discard.
 *
 * @returns `draft` when the row has never been saved, `dirty` when it has edits
 * the user hasn't saved, `applied` when it matches what bishop is filtering on.
 */
export function getRecordFilterRowState(
  condition: IRecordFilterCondition,
): TRecordFilterRowState {
  const { applied } = condition;
  if (!applied) return 'draft';
  return applied.field === condition.field &&
    applied.operator === condition.operator &&
    applied.value === condition.value
    ? 'applied'
    : 'dirty';
}

/**
 * Compiles the builder's rows into a `RowFilter` tree. Only the snapshot the user
 * confirmed counts, so an unapplied edit never narrows the table.
 *
 * @returns The tree, or undefined when no row has been applied yet
 */
export function buildRowFilter(
  conditions: IRecordFilterCondition[],
  logical: V1LogicalOperator,
  typeByField: Record<string, V1ValueScalarType | undefined>,
): V1RowFilter | undefined {
  const operands = conditions.reduce<V1RowFilter[]>((acc, { applied }) => {
    const node = applied && toFilterNode(applied, typeByField[applied.field]);
    if (node) acc.push(node);
    return acc;
  }, []);

  if (operands.length === 0) return undefined;
  return { logical: { operator: logical, operands } };
}

/**
 * Compiles checked rows into the `__row_id__ IN [...]` predicate the export RPC
 * takes in place of an id list, since `ExportCollection` has no `row_ids` field.
 *
 * The id's scalar is text or number depending on bishop's storage backend, so
 * the literals come from the rows' own `__row_id__` cells rather than being
 * re-encoded from the id strings.
 *
 * @returns The filter, or undefined when none of `rowIds` is present on `rows`.
 */
export function buildRowIdFilter(
  rows: V1CollectionRow[],
  rowIds: string[],
): V1RowFilter | undefined {
  const selected = new Set(rowIds);
  const items = rows.reduce<Commonv1Value[]>((acc, row) => {
    const rowId = getCollectionRowId(row);
    const value = row.fields?.__row_id__;
    if (rowId && value && selected.has(rowId)) acc.push(value);
    return acc;
  }, []);

  return items.length > 0
    ? {
        comparison: {
          field: '__row_id__',
          operator: 'COMPARISON_OPERATOR_IN',
          value: { list: { items } },
        },
      }
    : undefined;
}

/** Chip label for a date filter's value, e.g. `Sep 1, 2026 – Sep 15, 2026`. */
function formatDateFilterValue(
  draft: IRecordFilterDraft,
  t: TFunction,
): string {
  const format = (date: string) =>
    isValidDateStrict(date)
      ? formatJsDate(parseDateString(date), DATE_FORMAT_TOKENS.SHORT_DATE)
      : date;
  const trimmed = draft.value.trim();
  if (draft.operator === ERecordFilterOperator.Between) {
    const { from, to } = splitDateRangeISO(trimmed);
    return t('library.collection.records.filters.dateRange', {
      from: format(from),
      to: format(to),
    });
  }
  return format(trimmed);
}

/**
 * Chip label for one applied filter, e.g. `amount greater than "1000"` or
 * `due is before Sep 15, 2026`.
 */
export function formatFilterConditionSummary(
  draft: IRecordFilterDraft,
  type: V1ValueScalarType | undefined,
  t: TFunction,
): string {
  const operator = t(
    `library.collection.records.filters.operator.${getFilterOperatorLabelKey(draft.operator, type)}`,
  );
  const trimmed = draft.value.trim();
  // Quotes mark a literal string, so a number is left bare.
  const value =
    type === V1ValueScalarType.VALUE_SCALAR_TYPE_BOOLEAN
      ? t(
          `library.collection.records.filters.boolean.${trimmed === 'false' ? 'false' : 'true'}`,
        )
      : type === V1ValueScalarType.VALUE_SCALAR_TYPE_NUMBER
        ? trimmed
        : type === V1ValueScalarType.VALUE_SCALAR_TYPE_DATE
          ? formatDateFilterValue(draft, t)
          : `"${trimmed}"`;

  return `${draft.field} ${operator} ${value}`;
}

/**
 * The version of a collection its list card should show: the published one if
 * it has ever been published, otherwise the draft. The list is fetched at the
 * draft stage, so a published collection takes its stage and counts from
 * `preview`, which Bishop samples from the published version when there is one.
 *
 * @param collection - A collection from `ListCollections` at the draft stage
 * @returns The collection with `stage`, `field_count` and `record_count` of the version to show
 */
export function getDisplayedCollection(collection: V1Collection): V1Collection {
  const { preview } = collection;
  if (preview?.stage !== V1CollectionStage.COLLECTION_STAGE_PUBLISHED) {
    return collection;
  }
  return {
    ...collection,
    stage: V1CollectionStage.COLLECTION_STAGE_PUBLISHED,
    field_count: preview.field_count,
    record_count: preview.record_count,
  };
}
